<template>
    <!-- the latest articles: a spotlight that turns by itself (news page and home page) -->
    <section v-if="articles.length" class="news-spotlight" :class="{ 'news-spotlight--compact': compact }">
        <v-carousel
            v-model="slide"
            cycle
            interval="7000"
            hide-delimiters
            :show-arrows="articles.length > 1"
            show-arrows-on-hover
            :height="height"
            class="news-spotlight__carousel"
        >
            <v-carousel-item v-for="article in articles" :key="article.id">
                <router-link :to="{ name: 'Article', params: { id: article.id } }" class="news-slide">
                    <span class="news-slide__backdrop" :style="{ backgroundImage: `url('${getArticleImage(article)}')` }" />
                    <img :src="getArticleImage(article)" :alt="article.title" class="news-slide__image">
                    <span class="news-slide__shade" />
                    <span class="news-slide__caption">
                        <span class="news-slide__tags">
                            <span class="cl-chip cl-chip--gradient">Τελευταία νέα</span>
                            <span class="news-slide__date">{{ getArticleDate(article) }}</span>
                        </span>
                        <span class="news-slide__title">{{ article.title }}</span>
                        <span class="news-slide__excerpt">{{ getArticleExcerpt(article) }}</span>
                        <span class="news-slide__cta">Διάβασε το άρθρο <v-icon small>mdi-arrow-right</v-icon></span>
                    </span>
                </router-link>
            </v-carousel-item>
        </v-carousel>

        <!-- which article is on, and a way to pick another -->
        <div v-if="articles.length > 1" class="news-spotlight__dots">
            <button
                v-for="(article, index) in articles"
                :key="article.id"
                type="button"
                class="news-spotlight__dot"
                :class="{ 'news-spotlight__dot--active': slide === index }"
                :aria-label="article.title"
                @click="slide = index"
            />
        </div>
    </section>
</template>

<script>
    import momentTimezone from 'moment-timezone'
    import { articleExcerpt } from '@/components/ArticleCard'

    export default {
        name: 'NewsSpotlight',
        props: {
            articles: {
                type: Array,
                default: () => []
            },
            // a lower spotlight, for the home page
            compact: {
                type: Boolean,
                default: false
            }
        },
        data() {
            return {
                slide: 0
            }
        },
        computed: {
            height() {
                if (this.$vuetify.breakpoint.xs) return this.compact ? 340 : 380
                return this.compact ? 440 : 520
            }
        },
        methods: {
            getArticleImage(article) {
                return `https://storage.googleapis.com/${article.image}`
            },
            getArticleDate(article) {
                return momentTimezone(article.date).tz('Europe/Athens').locale('el').format('D MMMM YYYY')
            },
            getArticleExcerpt(article) {
                return articleExcerpt(article, 180)
            }
        }
    }
</script>

<style scoped>
/* ---------- spotlight ---------- */
.news-spotlight {
    position: relative;
    margin-bottom: 40px;
    border-radius: 14px;
    background: #232323;
    box-shadow: 0 16px 40px rgba(35, 35, 35, 0.18);
    overflow: hidden;
}

.news-spotlight--loading {
    height: 520px;
}

.news-spotlight::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    z-index: 2;
    height: 4px;
    background: linear-gradient(90deg, #03e5b7 0%, #037ade 74%);
}

.news-slide {
    position: relative;
    display: block;
    height: 100%;
    color: #fff !important;
    text-decoration: none;
    overflow: hidden;
}

.news-slide__backdrop {
    position: absolute;
    inset: 0;
    background-size: cover;
    background-position: center;
    filter: blur(24px) brightness(0.55);
    transform: scale(1.15);
}

.news-slide__image {
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    object-fit: contain;
}

/* the text reads on the photo: a shade from the bottom-left */
.news-slide__shade {
    position: absolute;
    inset: 0;
    background:
        linear-gradient(0deg, rgba(20, 20, 20, 0.95) 0%, rgba(20, 20, 20, 0.6) 35%, rgba(20, 20, 20, 0) 65%),
        linear-gradient(90deg, rgba(20, 20, 20, 0.55) 0%, rgba(20, 20, 20, 0) 55%);
}

.news-slide__caption {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    display: flex;
    flex-direction: column;
    gap: 10px;
    max-width: 820px;
    padding: 0 40px 44px;
    text-align: left;
}

.news-slide__tags {
    display: flex;
    align-items: center;
    gap: 10px;
}

.news-slide__date {
    font-family: Roboto, Arial, sans-serif;
    font-size: 0.8rem;
    font-weight: 700;
    letter-spacing: 0.04em;
    text-transform: uppercase;
    color: rgba(255, 255, 255, 0.75);
}

.news-slide__title {
    /* stylelint-disable value-no-vendor-prefix, property-no-vendor-prefix -- the line clamp needs the prefixed box */
    display: -webkit-box;
    -webkit-box-orient: vertical;
    /* stylelint-enable value-no-vendor-prefix, property-no-vendor-prefix */
    -webkit-line-clamp: 3;
    overflow: hidden;
    font-family: 'Squada One', 'Roboto Condensed', Arial, sans-serif;
    font-size: 2.8rem;
    line-height: 1.02;
    letter-spacing: 0.01em;
    text-shadow: 0 2px 18px rgba(0, 0, 0, 0.45);
}

.news-slide__excerpt {
    /* stylelint-disable value-no-vendor-prefix, property-no-vendor-prefix -- the line clamp needs the prefixed box */
    display: -webkit-box;
    -webkit-box-orient: vertical;
    /* stylelint-enable value-no-vendor-prefix, property-no-vendor-prefix */
    -webkit-line-clamp: 2;
    overflow: hidden;
    max-width: 680px;
    font-family: Roboto, Arial, sans-serif;
    font-size: 1rem;
    line-height: 1.5;
    color: rgba(255, 255, 255, 0.82);
}

.news-slide__cta {
    display: inline-flex;
    align-items: center;
    gap: 6px;
    align-self: flex-start;
    margin-top: 4px;
    padding: 8px 16px;
    border-radius: 8px;
    background: linear-gradient(315deg, #03e5b7 0%, #037ade 74%);
    font-family: Roboto, Arial, sans-serif;
    font-size: 0.85rem;
    font-weight: 700;
    box-shadow: 0 8px 20px rgba(3, 122, 222, 0.35);
}

.news-slide__cta .v-icon {
    color: #fff !important;
    transition: transform 0.15s;
}

.news-slide:hover .news-slide__cta .v-icon {
    transform: translateX(3px);
}

.news-spotlight__dots {
    position: absolute;
    right: 24px;
    bottom: 22px;
    z-index: 3;
    display: flex;
    gap: 6px;
}

.news-spotlight__dot {
    width: 10px;
    height: 10px;
    border-radius: 999px;
    background: rgba(255, 255, 255, 0.4);
    transition: width 0.2s, background 0.2s;
}

.news-spotlight__dot--active {
    width: 28px;
    background: #03e5b7;
}

.news-spotlight--compact {
    margin-bottom: 0;
}

.news-spotlight--compact .news-slide__title {
    font-size: 2.3rem;
}

@media (max-width: 700px) {
    .news-spotlight {
        margin-bottom: 28px;
        border-radius: 10px;
    }

    .news-slide__caption {
        gap: 8px;
        padding: 0 16px 40px;
    }

    .news-slide__title {
        font-size: 1.8rem;
    }

    .news-slide__excerpt {
        display: none;
    }

    .news-spotlight__dots {
        right: auto;
        left: 16px;
        bottom: 16px;
    }
}
</style>
